<script setup lang="ts">
/**
 * Singbac — DRIW First Board Resolutions (company-level).
 * "Singbac" 은 리터럴 텍스트(변수 아님). 주주(subscribers) 표는 행 추가/삭제 반복 UI
 * ([[if subscribers]]…[[/if subscribers]] 재현). 화면 키는 sub{id}_*.
 * 이사회 서명 = 동적 [[Signers]] (SgPreSigners, var-key="driw_Signers").
 */
import { onMounted, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import { useCddFill } from './cdd/useCddFill';
import SgPreSigners from './SgPreSigners.vue';
import './allotDoc.css';

const fill = useCddFill();

let seq = 1;
const subRows = ref<number[]>([1]);
function addSub() {
  subRows.value.push(++seq);
}
function delSub(id: number) {
  if (subRows.value.length > 1) subRows.value = subRows.value.filter((x) => x !== id);
}

/** 값이 채워진 sub{n} 행 id 목록 (편집 재진입 · 엔티티 자동시드 공용). */
function detectPresentRows(): number[] {
  const present: number[] = [];
  for (let i = 1; i <= 30; i++) {
    const has = ['member_name', 'member_shares', 'member_issue_price', 'member_issued_capital'].some(
      (s) => String(fill.values[`sub${i}_${s}`] ?? '').trim(),
    );
    if (has) present.push(i);
  }
  return present;
}

/** 채워진 행을 현재 행 목록에 병합(추가만) — 사용자가 추가한 빈 행은 유지. */
function syncPresentRows() {
  const present = detectPresentRows();
  if (!present.length) return;
  const merged = Array.from(new Set([...subRows.value, ...present])).sort((a, b) => a - b);
  if (merged.length !== subRows.value.length || merged.some((v, i) => v !== subRows.value[i])) {
    subRows.value = merged;
  }
  seq = Math.max(seq, ...merged);
}

// 편집 재진입 · 엔티티 선택 후 멤버 자동시드 → 채워진 subscriber 행을 노출한다.
onMounted(syncPresentRows);
watch(() => detectPresentRows().join(','), syncPresentRows);
</script>

<template>
  <div class="allot-doc">
    <!-- 회사 헤더 (docx 상단과 동일) -->
    <div class="doc-company-head">
      <p class="cn"><CddVarField field-key="company_name" label="Company Name" /></p>
      <p>COMPANY REGISTRATION NO.: <CddVarField field-key="company_registration_no" label="Company Registration No." /></p>
      <p>(the "Company")</p>
      <p class="inc"><i>Incorporated in the Republic of Singapore</i></p>
    </div>

    <div class="doc-sec-title">DIRECTORS' RESOLUTIONS IN WRITING PURSUANT TO THE CONSTITUTION OF THE COMPANY</div>

    <p class="doc-lbl-title">INCORPORATION OF COMPANY</p>
    <p class="doc-p">
      NOTED that the Company was incorporated on <CddVarField field-key="incorporation_date" label="Incorporation Date" />
      and that the registration number assigned to it is
      <CddVarField field-key="company_registration_no" label="Company Registration No." />.
    </p>

    <p class="doc-lbl-title">FIRST DIRECTORS</p>
    <p class="doc-p">
      NOTED that <CddVarField field-key="first_director_names" label="First Directors" />, the persons named as first
      Directors have accepted the office of Director of the Company.
    </p>

    <p class="doc-lbl-title">REGISTERED OFFICE</p>
    <p class="doc-p">
      RESOLVED that, with effect from the date of incorporation of the Company, the registered office of the Company be
      situated at:
    </p>
    <p class="doc-p"><CddVarField field-key="registered_office_address" label="Registered Office Address" /></p>

    <p class="doc-lbl-title">REGISTRATION OF SUBSCRIBERS</p>
    <p class="doc-p">
      RESOLVED that the following members (the subscribers to the Constitution) be registered as the holders of the number
      of shares set out against their names in the capital of the Company, issued at the issue price set out below:
    </p>
    <!-- repeat: subscribers ([[if subscribers]] … [[/if subscribers]]) -->
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <th>Name of Member</th>
          <th>Number of Shares Subscribed</th>
          <th>Issue Price per share</th>
          <th>Issued Share Capital</th>
        </tr>
        <tr v-for="(id, i) in subRows" :key="id">
          <td>
            <CddVarField :field-key="`sub${id}_member_name`" :label="`Member ${i + 1} Name`" />
            <button v-if="subRows.length > 1" class="row-del" title="Remove" @click="delSub(id)">✕</button>
          </td>
          <td><CddVarField :field-key="`sub${id}_member_shares`" :label="`Member ${i + 1} Shares`" /></td>
          <td><CddVarField :field-key="`sub${id}_member_issue_price`" :label="`Member ${i + 1} Issue Price`" /></td>
          <td>SGD <CddVarField :field-key="`sub${id}_member_issued_capital`" :label="`Member ${i + 1} Capital`" /></td>
        </tr>
        <tr><td colspan="4"><button class="row-add" @click="addSub">+ Add subscriber</button></td></tr>
      </tbody>
    </table>
    <p class="doc-p">
      FURTHER RESOLVED that a share certificate be issued accordingly to each of the subscribers in accordance with the
      Constitution of the Company and signed by any one director and the secretary of the company.
    </p>

    <p class="doc-lbl-title">APPOINTMENT OF COMPANY SECRETARY &amp; GENERAL AUTHORITY TO NOMINATE SECRETARY OF THE COMPANY</p>
    <p class="doc-p">RESOLVED that:</p>
    <p class="doc-p indent">
      the appointment of <CddVarField field-key="secretary_name" label="Secretary Name" /> as Secretary of the Company
      with effect from the date of incorporation of the Company be noted;
    </p>
    <p class="doc-p indent">
      approval be given for any changes made by Singbac Pte. Ltd. ("Singbac") from time to time in the secretaries
      nominated by Singbac during the course of the provision of corporate secretarial services to the Company, including
      any removal or resignation of any named secretary, and any appointment of a new named secretary, and that any such
      change including the appointment of a new secretary shall be deemed to be approved and accepted by the Company,
      without requiring any further Directors' or shareholders' resolution of the Company;
    </p>
    <p class="doc-p indent">
      Singbac be hereby authorised to notify the Accounting and Corporate Regulatory Authority at the relevant time or
      times of such change; and
    </p>
    <p class="doc-p indent">
      in the event of the termination of Singbac's corporate secretarial services to the Company or vice versa, Singbac be
      further authorised on behalf of the Company to notify the Accounting and Corporate Regulatory Authority of the
      resignation of any such secretaries nominated by Singbac.
    </p>
    <p class="doc-p">
      FURTHER RESOLVED that <CddVarField field-key="secretary_name" label="Secretary Name" /> and any new appointed named
      Secretary nominated by Singbac from time to time, shall not be involved in the administration, operations or
      management of the Company but shall be authorised to register as the CorpPass Administrator of the Company for the
      purpose of granting users access as required by the Company and/or to grant access to relevant personnel in Singbac
      for the purpose of carrying out their required duties as engaged by the Company.
    </p>

    <p class="doc-lbl-title">FINANCIAL YEAR END</p>
    <p class="doc-p">
      RESOLVED that the first financial period of the Company shall be from the date of incorporation, i.e.,
      <CddVarField field-key="first_financial_period" label="First Financial Period" /> and that, thereafter, unless
      otherwise resolved, the financial year of the Company shall end on
      <CddVarField field-key="financial_year_end" label="Financial Year End" /> of every succeeding year.
    </p>

    <p class="doc-lbl-title">ESTABLISHMENT OF BANK ACCOUNT</p>
    <p class="doc-p">RESOLVED that:</p>
    <p class="doc-p indent">
      the Company may open a bank account (the "Account") in the name of the Company with a Singapore Bank, Overseas Bank
      and/or Digital Bank (the "Bank");
    </p>
    <p class="doc-p indent">
      the Bank's standard form of resolutions and/or general mandate be hereby approved and shall be deemed to form part
      of this resolution;
    </p>
    <p class="doc-p indent">
      the authorised signatory(ies) and the operation of the Account as submitted to the Bank be and is hereby approved,
      confirmed and in all respects ratified;
    </p>
    <p class="doc-p indent">
      any Director, officer and/or authorised person(s) of the Company be authorised to sign all relevant documents
      required to effect the establishment of the Account; and
    </p>
    <p class="doc-p indent">
      all actions taken by the Directors, officers and/or authorised person(s) of the Company relating to the
      establishment of the Account prior to the passing of this Resolution be hereby approved, confirmed and in all
      respects ratified.
    </p>

    <p class="doc-lbl-title">NOTIFICATIONS TO THE ACCOUNTING &amp; CORPORATE REGULATORY AUTHORITY</p>
    <p class="doc-p">
      RESOLVED that the Secretary and/or Messrs Singbac Pte Ltd be authorised to complete and file all returns to be made
      to the Accounting &amp; Corporate Regulatory Authority.
    </p>

    <p class="doc-p"><b>Dated:</b> <CddVarField field-key="driw_date" label="Resolution Date" /></p>

    <!-- 이사회 서명 — 이사 인원 가변 → 동적 서명 블록([[Signers]]) -->
    <p class="doc-lbl-title">DIRECTORS</p>
    <SgPreSigners var-key="driw_Signers" board-label="DIRECTORS" />
  </div>
</template>

<style scoped>
.doc-lbl-title { font-weight: 800; margin: 14px 0 6px; }
.doc-company-head { text-align: center; margin: 0 0 16px; line-height: 1.5; }
.doc-company-head .cn { font-weight: 800; }
.doc-company-head p { margin: 0; }
.doc-company-head .inc { margin-top: 2px; }
.row-del { margin-left: 6px; border: none; background: none; color: #c0392b; cursor: pointer; font-size: 11px; }
.row-add { border: 1px dashed #9aa4b2; background: #f7f8fa; border-radius: 6px; padding: 2px 8px; font-size: 11px; color: #3182F6; cursor: pointer; }
</style>
